import React from 'react';
import PropTypes from 'prop-types';

import { Container, Typography, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Icon, Translate } from '@hydra/common/components';
import ColorPreview from '@hydra/routes/pdf-editor/components/ColorPreview';
import EditorFormRow from '@hydra/routes/pdf-editor/components/EditorFormRow/EditorFormRow';
import Editor from '@hydra/routes/pdf-editor/components/EditorFormRow/Editor';
import EditorInline from '@hydra/routes/pdf-editor/components/EditorFormRow/EditorInline';
import FormDropdown from '@hydra/routes/pdf-editor/components/FormDropdown';
import FormRadioButtons from '@hydra/routes/pdf-editor/components/FormRadioButtons';
import FormTextInput from '@hydra/routes/pdf-editor/components/FormTextInput';
import FormToggle from '@hydra/routes/pdf-editor/components/FormToggle';
import ColorPickers from '@hydra/routes/pdf-editor/components/ColorPickers';
import HelpText from '@hydra/routes/pdf-editor/components/HelpText';
import Header from '@hydra/routes/pdf-editor/components/Header';
import { MAX_TEMPLATE_NAME_LENGTH } from '@hydra/routes/pdf-editor/utils/pdfEditorUtils';

const useStyles = makeStyles(theme => ({
  orientationLabel: {
    display: 'flex',
    alignItems: 'center'
  }
}));

export const TabGeneral = ({
  template,
  editing,
  editingValue,
  setEditingValue,
  setEditingField,
  resetEditing,
  saveFormField,
  headerLocked,
  featuresLocked
}) => {
  const classes = useStyles();

  const languageItems = [
    { value: 'de', content: <Translate content='lang_de' /> },
    { value: 'en', content: <Translate content='lang_en' /> },
    { value: 'es', content: <Translate content='lang_es' /> },
    { value: 'fr', content: <Translate content='lang_fr' /> },
    { value: 'nl', content: <Translate content='lang_nl' /> },
    { value: 'pt', content: <Translate content='lang_pt' /> }
  ];

  const formatItems = [
    { value: 'a4', content: <Translate content='pdf_editor_a4' /> },
    { value: 'letter', content: <Translate content='pdf_editor_us' /> }
  ];

  const orientationItems = [
    {
      value: 'portrait',
      content: (
        <Typography className={classes.orientationLabel}>
          <Translate content='pdf_editor_portrait' />
          <IconButton component='span' disabled>
            <Icon type='asset-portrait' />
          </IconButton>
        </Typography>
      )
    },
    {
      value: 'landscape',
      content: (
        <Typography className={classes.orientationLabel}>
          <Translate content='pdf_editor_landscape' />
          <IconButton component='span' disabled>
            <Icon type='asset-landscape' />
          </IconButton>
        </Typography>
      )
    }
  ];

  const themeItems = [
    { content: <Translate content='pdf_editor_theme_modern' />, value: 'modern' },
    { content: <Translate content='pdf_editor_theme_classic' />, value: 'classic' }
  ];

  return (
    <>
      <Header title='pdf_editor_tab_general' hasLockedButton={headerLocked} />
      <Container>
        <EditorFormRow
          label={<Translate content='pdf_editor_template_name' />}
          help={<HelpText content='pdf_editor_option_help_template_name' />}
        >
          <Editor
            field='name'
            maxLength={MAX_TEMPLATE_NAME_LENGTH}
            Component={FormTextInput}
            preview={<span>{template.name}</span>}
            value={template.name}
            editingValue={editingValue}
            editing={editing === 'name'}
            otherFieldOpen={(editing !== null && editing !== 'name')}
            setEditingValue={setEditingValue}
            setEditingField={setEditingField}
            resetEditing={resetEditing}
            saveFormField={saveFormField}
          />
        </EditorFormRow>

        <EditorFormRow
          label={<Translate content='language' />}
          help={<HelpText content='pdf_editor_option_help_language' />}
        >
          <EditorInline
            Component={FormDropdown}
            items={languageItems}
            field='language'
            value={template.language}
            otherFieldOpen={(editing !== null && editing !== 'language')}
            saveFormField={saveFormField}
          />
        </EditorFormRow>

        <EditorFormRow
          label={<Translate content='pdf_editor_orientation' />}
          help={<HelpText content='pdf_editor_option_help_orientation' />}
        >
          <EditorInline
            Component={FormRadioButtons}
            items={orientationItems}
            field='orientation'
            value={template.orientation}
            otherFieldOpen={(editing !== null && editing !== 'orientation')}
            saveFormField={saveFormField}
          />
        </EditorFormRow>

        <EditorFormRow
          label={<Translate content='pdf_editor_format' />}
          help={<HelpText content='pdf_editor_option_help_format' />}
        >
          <EditorInline
            Component={FormRadioButtons}
            items={formatItems}
            field='size'
            value={template.size}
            otherFieldOpen={(editing !== null && editing !== 'size')}
            saveFormField={saveFormField}
          />
        </EditorFormRow>

        <EditorFormRow
          label={<Translate content='pdf_editor_theme' />}
          help={<HelpText content='pdf_editor_option_help_theme' />}
        >
          <EditorInline
            Component={FormDropdown}
            items={themeItems}
            field='theme'
            value={template.theme}
            otherFieldOpen={(editing !== null && editing !== 'theme')}
            saveFormField={saveFormField}
          />
        </EditorFormRow>

        <EditorFormRow
          label={<Translate content='pdf_editor_table_of_content' />}
          help={<HelpText content='pdf_editor_option_help_table_of_content' />}
          locked={featuresLocked}
          hasLockedButton={featuresLocked}
        >
          <EditorInline
            Component={FormToggle}
            field='tableOfContentEnabled'
            value={template.tableOfContentEnabled}
            otherFieldOpen={(editing !== null && editing !== 'tableOfContentEnabled')}
            saveFormField={saveFormField}
            locked={featuresLocked}
          />
        </EditorFormRow>

        <EditorFormRow
          label={<Translate content='pdf_editor_primary_color' />}
          help={<HelpText content='pdf_editor_option_help_primary_color_2' />}
          locked={featuresLocked}
          hasLockedButton={featuresLocked}
        >
          <Editor
            field='colorScheme.primaryColor'
            preview={<ColorPreview colors={(template.colorScheme || {}).primaryColor} />}
            Component={ColorPickers}
            value={template.colorScheme.primaryColor}
            editingValue={editingValue}
            editing={editing === 'colorScheme.primaryColor'}
            otherFieldOpen={(editing !== null && editing !== 'colorScheme.primaryColor')}
            setEditingValue={setEditingValue}
            setEditingField={setEditingField}
            resetEditing={resetEditing}
            locked={featuresLocked}
            saveFormField={({ id, value }) => saveFormField({
              id,
              field: 'colorScheme',
              value: {
                ...template.colorScheme,
                primaryColor: value
              }
            })}
          />
        </EditorFormRow>

        <EditorFormRow
          label={<Translate content='pdf_editor_secondary_colors' />}
          help={<HelpText content='pdf_editor_option_help_secondary_color' />}
          locked={featuresLocked}
          hasLockedButton={featuresLocked}
        >
          <Editor
            field='colorScheme.secondaryColors'
            preview={<ColorPreview colors={(template.colorScheme || {}).secondaryColors} />}
            Component={ColorPickers}
            value={template.colorScheme.secondaryColors}
            editingValue={editingValue}
            editing={editing === 'colorScheme.secondaryColors'}
            otherFieldOpen={(editing !== null && editing !== 'colorScheme.secondaryColors')}
            setEditingValue={setEditingValue}
            setEditingField={setEditingField}
            resetEditing={resetEditing}
            locked={featuresLocked}
            saveFormField={({ value }) => saveFormField({
              field: 'colorScheme',
              value: {
                ...template.colorScheme,
                secondaryColors: value
              }
            })}
          />
        </EditorFormRow>

        <EditorFormRow
          label={<Translate content='pdf_editor_external_links' />}
          help={<HelpText content='pdf_editor_option_help_external_links' />}
          locked={featuresLocked}
          hasLockedButton={featuresLocked}
        >
          <EditorInline
            Component={FormToggle}
            field='externalLinks'
            value={template.externalLinks}
            otherFieldOpen={(editing !== null && editing !== 'externalLinks')}
            saveFormField={saveFormField}
            locked={featuresLocked}
          />
        </EditorFormRow>
      </Container>
    </>
  );
};

TabGeneral.propTypes = {
  template: PropTypes.shape({
    name: PropTypes.string
  })
};

export default React.memo(TabGeneral);
